/**
 * `/schedules` — jobs run on a cron schedule.
 *
 * Each row: what it runs, when (the expression and the zone it is read in),
 * when it runs next, how the last run ended. The name opens the run history.
 * Run now, edit and delete come from `../entities/scheduleActions`, the same
 * set the detail screen offers. Creating is this screen's own act.
 */
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type { ScheduleDto } from '@forsys/shared';
import { createSchedule, listScheduleJobs, listSchedules, updateSchedule } from '../../api/schedules';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import {
  ScheduleFields,
  blankScheduleForm,
  createRequestFrom,
  scheduleIncomplete,
  useScheduleActions,
  type ScheduleForm,
} from '../entities/scheduleActions';
import { RunStatusChip } from './RunStatusChip';

export default function SchedulesPage() {
  const { t } = useTranslation('schedules');
  const { can } = useAuth();
  const list = useListQuery({ defaultSort: 'name' });
  const schedules = useAsync(() => listSchedules(list.query), [list.key]);
  const jobs = useAsync(() => listScheduleJobs(), []);
  const jobList = jobs.data?.jobs ?? [];
  const mayManage = can('schedule.manage');

  const actions = useScheduleActions({
    jobs: jobList,
    onChanged: () => schedules.reload(),
    onDeleted: () => schedules.reload(),
  });

  // Creating only: `formFor` is inert, editing goes through `actions`.
  const crud = useCrudDialogs<ScheduleDto, ScheduleForm>({
    blank: blankScheduleForm(),
    formFor: () => blankScheduleForm(),
  });
  const { form } = crud;

  const create = useSubmit(async () => {
    const created = await createSchedule(createRequestFrom(form));
    schedules.reload();
    return created;
  });

  // The switch is the reversible half of deleting, so it acts at once.
  const toggle = useSubmit(async (schedule: ScheduleDto) => {
    const result = await updateSchedule(schedule.id, { isEnabled: !schedule.isEnabled });
    schedules.reload();
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          mayManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => crud.openCreate()}>
              {t('new')}
            </Button>
          )
        }
      />

      {toggle.error && <ErrorState error={toggle.error} />}
      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={schedules}>
        {({ schedules: rows, page }) => (
          <EntityTable
            rows={rows}
            rowKey={(schedule) => schedule.id}
            list={list}
            page={page}
            empty={{
              icon: <ScheduleIcon fontSize="inherit" />,
              title: t('emptyTitle'),
              description: t('emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('column.name'),
                sortable: true,
                render: (schedule) => (
                  <Link
                    component={RouterLink}
                    to={`/schedules/${schedule.id}`}
                    underline="hover"
                    color="inherit"
                    sx={{ fontWeight: 500 }}
                  >
                    {schedule.name}
                  </Link>
                ),
              },
              {
                field: 'job',
                label: t('column.job'),
                width: 170,
                render: (schedule) => (
                  <Chip size="small" variant="outlined" label={schedule.job} sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                ),
              },
              {
                field: 'cron',
                label: t('column.when'),
                width: 190,
                render: (schedule) => (
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {schedule.cron}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {schedule.timeZone}
                    </Typography>
                  </Stack>
                ),
              },
              {
                field: 'nextRunAt',
                label: t('column.nextRun'),
                sortable: true,
                width: 150,
                render: (schedule) =>
                  schedule.nextRunAt ? (
                    <Timestamp value={schedule.nextRunAt} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('disabled')}
                    </Typography>
                  ),
              },
              {
                field: 'lastRun',
                label: t('column.lastRun'),
                width: 170,
                render: (schedule) =>
                  schedule.lastStatus && schedule.lastRunAt ? (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <RunStatusChip status={schedule.lastStatus} />
                      <Timestamp value={schedule.lastRunAt} />
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('neverRun')}
                    </Typography>
                  ),
              },
              {
                field: 'enabled',
                label: t('column.enabled'),
                width: 90,
                render: (schedule) => (
                  <Switch
                    size="small"
                    checked={schedule.isEnabled}
                    disabled={!mayManage || toggle.pending}
                    onChange={() => void toggle.submit(schedule)}
                  />
                ),
              },
            ]}
            actionsWidth={136}
            actions={actions.rowActions}
          />
        )}
      </AsyncContent>

      {actions.dialogs}

      <FormDialog
        open={crud.isOpen}
        title={t('dialog.title')}
        submitLabel={t('dialog.submit')}
        maxWidth="sm"
        pending={create.pending}
        error={create.error}
        disabled={scheduleIncomplete(form)}
        onClose={crud.close}
        onSubmit={async () => {
          if (await create.submit()) crud.close();
        }}
      >
        <ScheduleFields form={form} patch={crud.patch} jobs={jobList} />
      </FormDialog>
    </>
  );
}
